import { IconButton, Stack, Typography } from '@mui/material';

import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import StarIcon from '@mui/icons-material/Star';
import CircleIcon from '@mui/icons-material/Circle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import useBoardStore from '../../store/boardStore';
import useUserStore from '../../store/userStore';
import type { Idea } from '../../interfaces/Idea';

const getVoteIconType = (voteIconType: string | undefined) => {
  if (voteIconType === 'thumb' || voteIconType === 'star' || voteIconType === 'dot') {
    return voteIconType;
  }

  return 'heart';
};

export interface IdeaCardVotesProps {
  idea: Idea;
  size?: 'default' | 'large';
  layout?: 'default' | 'horizontal';
  totalCountBackgroundColor?: string;
  hideVoteOwners?: boolean;
}

const IdeaCardVotes = ({
  idea,
  size = 'default',
  layout = 'default',
  totalCountBackgroundColor,
  hideVoteOwners = false,
}: IdeaCardVotesProps) => {
  const currentUser = useUserStore((state) => state.getCurrentUser());
  const participants = useBoardStore((state) => state.participants);
  const canInteractWithVotes = Boolean(currentUser);
  const userVotes =
    currentUser != null ? idea.votes.filter((vote) => vote.ownerId === currentUser.id) : [];
  const hasExceededLimit = useBoardStore((state) =>
    currentUser ? state.hasExceededVoteLimit(currentUser.id) : false
  );
  const removeVote = useBoardStore((state) => state.removeVote);
  const addVote = useBoardStore((state) => state.addVote);
  const boardDetails = useBoardStore((state) => state.boardDetails);
  const showVoteOwners = useBoardStore((state) => state.showVoteOwners);
  if (!boardDetails) {
    return null;
  }

  if (boardDetails.cardsRequireApproval === true && idea.approved === false) {
    return null;
  }

  if (boardDetails.votingStyle === 'none') {
    return null;
  }

  const isBoardLocked = boardDetails.locked === true;
  const votingEnabled = boardDetails.votingEnabled !== false;
  const isLikingStyle = boardDetails.votingStyle === 'liking';
  const voteIconType = getVoteIconType(boardDetails.voteIconType);
  const myVoteColor = boardDetails.myVoteColor || '#ffa2a2';
  const theirVoteColor = boardDetails.theirVoteColor || '#ff0000';
  const hideVoteTotals = boardDetails.privateVotingEnabled === true && !boardDetails.isOwner;
  const canShowVoteOwners =
    !hideVoteOwners && !isLikingStyle && boardDetails.isOwner && showVoteOwners;
  const userLike = userVotes[0] ?? null;
  const showVotingControls =
    votingEnabled &&
    canInteractWithVotes &&
    !isLikingStyle &&
    (userVotes.length > 0 || (!hasExceededLimit && !isBoardLocked));

  const renderVoteIcon = (filled: boolean, color?: string) => {
    const iconSize = size === 'default' ? 'small' : 'medium';
    const iconSx = color ? { color } : undefined;

    if (voteIconType === 'thumb') {
      return filled ? (
        <ThumbUpIcon fontSize={iconSize} sx={iconSx} />
      ) : (
        <ThumbUpOutlinedIcon fontSize={iconSize} sx={iconSx} />
      );
    }

    if (voteIconType === 'star') {
      return filled ? (
        <StarIcon fontSize={iconSize} sx={{ ...iconSx, stroke: '#9e9e9e', strokeWidth: 1 }} />
      ) : (
        <StarIcon
          fontSize={iconSize}
          sx={{ color: '#ffffff', stroke: '#9e9e9e', strokeWidth: 1 }}
        />
      );
    }

    if (voteIconType === 'dot') {
      return filled ? (
        <CircleIcon fontSize={iconSize} sx={iconSx} />
      ) : (
        <RadioButtonUncheckedIcon fontSize={iconSize} sx={iconSx} />
      );
    }

    return filled ? (
      <FavoriteIcon fontSize={iconSize} sx={iconSx} />
    ) : (
      <FavoriteBorderIcon fontSize={iconSize} sx={iconSx} />
    );
  };

  const renderVoteCount = () => {
    const showContent = isLikingStyle || (!isLikingStyle && !hideVoteTotals);

    if (!showContent) {
      return null;
    }

    return (
      <Stack
        direction="row"
        gap={0.5}
        alignItems="center"
        flexWrap="wrap"
        sx={{
          backgroundColor: totalCountBackgroundColor ?? 'transparent',
          borderRadius: 1,
          px: 0,
          py: 0.25,
        }}
      >
        {isLikingStyle && votingEnabled && canInteractWithVotes ? (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();

              if (userLike) {
                void removeVote(idea.id, userLike.id);
                return;
              }

              void addVote(idea.id);
            }}
            aria-label={userLike ? 'Unlike this card' : 'Like this card'}
            sx={{ p: 0 }}
            disabled={isBoardLocked}
          >
            {userLike
              ? renderVoteIcon(true, theirVoteColor)
              : renderVoteIcon(false, theirVoteColor)}
          </IconButton>
        ) : (
          !hideVoteTotals && renderVoteIcon(true, theirVoteColor)
        )}
        {!hideVoteTotals && (
          <Typography
            variant={size === 'default' ? 'caption' : 'h6'}
            sx={
              size === 'default'
                ? { fontSize: 15, fontWeight: 700, color: '#000' }
                : { color: '#000' }
            }
          >
            {idea.votes.length}
          </Typography>
        )}
      </Stack>
    );
  };

  const renderVotingButtons = () => {
    return (
      <Stack direction="row" gap={0} alignItems="center">
        {userVotes.map((vote) => (
          <IconButton
            key={vote.id}
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              void removeVote(idea.id, vote.id);
            }}
            aria-label="Remove your vote"
            sx={{ padding: 0 }}
            disabled={isBoardLocked}
          >
            {renderVoteIcon(true, myVoteColor)}
          </IconButton>
        ))}
        {!hasExceededLimit && !isBoardLocked && (
          <IconButton
            key={`add-vote-${idea.id}`}
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              void addVote(idea.id);
            }}
            aria-label="Add your vote"
            sx={{ padding: 0 }}
          >
            {renderVoteIcon(false, theirVoteColor)}
          </IconButton>
        )}
      </Stack>
    );
  };

  const voteOwnerCounts = canShowVoteOwners
    ? Array.from(
        idea.votes
          .map(
            (vote) =>
              participants.find((participant) => participant.id === vote.ownerId)?.name?.trim() ??
              vote.ownerId
          )
          .reduce((counts, ownerName) => {
            const currentCount = counts.get(ownerName) ?? 0;
            counts.set(ownerName, currentCount + 1);
            return counts;
          }, new Map<string, number>())
      )
    : [];

  const renderVoteOwners = () => {
    if (!canShowVoteOwners || voteOwnerCounts.length === 0) {
      return null;
    }

    return (
      <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center" sx={{ mt: 0.5, mb: 1 }}>
        {voteOwnerCounts.map(([ownerName, voteCount]) => (
          <Typography
            key={`${idea.id}-vote-owner-${ownerName}`}
            variant={size === 'default' ? 'caption2' : 'caption'}
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: '#f3f3f3',
              color: '#1d1d1d',
              fontWeight: 500,
            }}
          >
            {voteCount > 1 ? `${ownerName} x ${voteCount}` : ownerName}
          </Typography>
        ))}
      </Stack>
    );
  };

  if (layout === 'horizontal') {
    return (
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        {renderVoteCount()}
        {renderVoteOwners()}
        {showVotingControls && renderVotingButtons()}
      </Stack>
    );
  }

  return (
    <Stack gap={0.25}>
      {renderVoteCount()}
      {renderVoteOwners()}
      {showVotingControls && renderVotingButtons()}
    </Stack>
  );
};

export default IdeaCardVotes;
